// BlogAdmin.js
import React, { useState, useEffect } from 'react';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  orderBy, 
  serverTimestamp 
} from 'firebase/firestore';

import { db } from '../firebase/firebase';

// TipTap Editor imports
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import  Header  from '../components/Header';

// Optional: custom editor toolbar component
const EditorToolbar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="editor-toolbar border rounded p-2 mb-2 bg-light">
      <div className="btn-toolbar" role="toolbar">
        <div className="btn-group me-2" role="group">
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('heading', { level: 1 }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          >
            H1
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('heading', { level: 2 }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            H2
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('heading', { level: 3 }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          >
            H3
          </button>
        </div>

        <div className="btn-group me-2" role="group">
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('bold') ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <i className="fas fa-bold"></i>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('italic') ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <i className="fas fa-italic"></i>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('underline') ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <i className="fas fa-underline"></i>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('strike') ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleStrike().run()}
          >
            <i className="fas fa-strikethrough"></i>
          </button>
        </div>

        <div className="btn-group me-2" role="group">
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('bulletList') ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <i className="fas fa-list-ul"></i>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('orderedList') ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <i className="fas fa-list-ol"></i>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive('blockquote') ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
          >
            <i className="fas fa-quote-right"></i>
          </button>
        </div>

        <div className="btn-group me-2" role="group">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              const url = window.prompt('URL');
              if (url) {
                editor.chain().focus().setLink({ href: url }).run();
              }
            }}
          >
            <i className="fas fa-link"></i>
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => {
              const url = window.prompt('Image URL');
              if (url) {
                editor.chain().focus().setImage({ src: url }).run();
              }
            }}
          >
            <i className="fas fa-image"></i>
          </button>
        </div>

        <div className="btn-group me-2" role="group">
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive({ textAlign: 'left' }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
          >
            <i className="fas fa-align-left"></i>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive({ textAlign: 'center' }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
          >
            <i className="fas fa-align-center"></i>
          </button>
          <button
            type="button"
            className={`btn btn-sm ${editor.isActive({ textAlign: 'right' }) ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
          >
            <i className="fas fa-align-right"></i>
          </button>
        </div>

        <div className="btn-group" role="group">
          <input
            type="color"
            className="form-control form-control-sm"
            style={{ width: '40px', padding: '0 2px' }}
            onChange={(e) => {
              editor.chain().focus().setColor(e.target.value).run();
            }}
          />
        </div>
      </div>
    </div>
  );
};

const BlogAdmin = () => {
  // eslint-disable-next-line no-unused-vars
  const [posts, setPosts] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [currentPostId, setCurrentPostId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    category: '',
    tags: '',
    author: '',
    authorBio: '',
    imageFile: null,
    imageUrl: '',
    base64Image: '' // New field to store base64 image
  });
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  // Initialize TipTap editor
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link,
      Image,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      Placeholder.configure({
        placeholder: 'Write your blog post content here...',
      }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData({
        ...formData,
        content: editor.getHTML()
      });
    },
  });

  // Update editor content when formData.content changes (for edit mode)
  useEffect(() => {
    if (editor && formData.content && editor.getHTML() !== formData.content) {
      editor.commands.setContent(formData.content);
    }
  }, [editor, formData.content]);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const postsQuery = query(collection(db, 'blogPosts'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(postsQuery);
      const postsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setPosts(postsData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching posts:", error);
      setError('Failed to load posts');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Modified function to handle image change and convert to base64
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setFormData({
        ...formData,
        imageFile: file
      });
      
      // Convert image to base64 string
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prevState => ({
          ...prevState,
          base64Image: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Modified to use base64 instead of storage upload
  const uploadImage = async () => {
    if (!formData.imageFile) return '';
    
    setUploadingImage(true);
    
    try {
      // Return the base64 string directly
      setUploadingImage(false);
      setProgress(100);
      return formData.base64Image;
    } catch (error) {
      console.error("Error in uploadImage:", error);
      setError('Image conversion failed');
      setUploadingImage(false);
      return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!formData.title || !formData.content) {
      setError('Title and content are required');
      return;
    }
    
    try {
      // Process tags
      const tagList = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag);
      
      // Upload image if there's a new one
      let imageUrl = formData.imageUrl;
      if (formData.imageFile) {
        imageUrl = await uploadImage();
      }
      
      const postData = {
        title: formData.title,
        summary: formData.summary,
        content: formData.content,
        category: formData.category,
        tags: tagList,
        author: formData.author,
        authorBio: formData.authorBio,
        imageUrl, // This will now be the base64 string
        updatedAt: serverTimestamp()
      };
      
      if (formMode === 'create') {
        // Add createdAt for new posts
        postData.createdAt = serverTimestamp();
        
        // Add to Firestore
        const docRef = await addDoc(collection(db, 'blogPosts'), postData);
        setSuccess(`Post created with ID: ${docRef.id}`);
      } else {
        // Update existing post
        await updateDoc(doc(db, 'blogPosts', currentPostId), postData);
        setSuccess('Post updated successfully');
      }
      
      // Reset form and refresh posts
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error("Error saving post:", error);
      setError('Failed to save post');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleEdit = (post) => {
    setFormMode('edit');
    setCurrentPostId(post.id);
    setFormData({
      title: post.title || '',
      summary: post.summary || '',
      content: post.content || '',
      category: post.category || '',
      tags: (post.tags || []).join(', '),
      author: post.author || '',
      authorBio: post.authorBio || '',
      imageFile: null,
      imageUrl: post.imageUrl || '',
      base64Image: post.imageUrl || '' // Set the existing image URL as base64 for consistency
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // eslint-disable-next-line no-unused-vars
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await deleteDoc(doc(db, 'blogPosts', id));
        setSuccess('Post deleted successfully');
        fetchPosts();
      } catch (error) {
        console.error("Error deleting post:", error);
        setError('Failed to delete post');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      summary: '',
      content: '',
      category: '',
      tags: '',
      author: '',
      authorBio: '',
      imageFile: null,
      imageUrl: '',
      base64Image: ''
    });
    
    // Reset editor content
    if (editor) {
      editor.commands.setContent('');
    }
    
    setFormMode('create');
    setCurrentPostId(null);
    setProgress(0);
  };

  return (
    <div>
      <Header/>
      
      <section className="blog-admin">
        <div className="container pt-5 mt-5"> {/* Added pt-5 and mt-5 for proper spacing from navbar */}
          <h1 className="mb-4">{formMode === 'create' ? 'Create New Post' : 'Edit Post'}</h1>
          
          {error && <div className="alert alert-danger">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          
          <form onSubmit={handleSubmit} className="mb-5">
            <div className="mb-3">
              <label htmlFor="title" className="form-label">Title *</label>
              <input
                type="text"
                className="form-control"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="summary" className="form-label">Summary</label>
              <textarea
                className="form-control"
                id="summary"
                name="summary"
                rows="3"
                value={formData.summary}
                onChange={handleInputChange}
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="category" className="form-label">Category</label>
              <select
                className="form-select"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">Select a category</option>
                <option value="Technology">Technology</option>
                <option value="Business">Business</option>
                <option value="Lifestyle">Lifestyle</option>
                <option value="Health">Health</option>
                <option value="Travel">Travel</option>
                <option value="Food">Food</option>
                <option value="Education">Education</option>
                <option value="Other">Other</option>
              </select>
            </div>
            
            <div className="mb-3">
              <label htmlFor="tags" className="form-label">Tags (comma separated)</label>
              <input
                type="text"
                className="form-control"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                placeholder="e.g. javascript, react, firebase"
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="author" className="form-label">Author</label>
              <input
                type="text"
                className="form-control"
                id="author"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="authorBio" className="form-label">Author Bio</label>
              <textarea
                className="form-control"
                id="authorBio"
                name="authorBio"
                rows="3"
                value={formData.authorBio}
                onChange={handleInputChange}
              ></textarea>
            </div>
            
            <div className="mb-3">
              <label htmlFor="imageFile" className="form-label">Featured Image</label>
              <input
                type="file"
                className="form-control"
                id="imageFile"
                accept="image/*"
                onChange={handleImageChange}
              />
              
              {(formData.imageUrl || formData.base64Image) && (
                <div className="mt-2">
                  <img 
                    src={formData.base64Image || formData.imageUrl} 
                    alt="Featured" 
                    style={{maxHeight: '200px'}} 
                    className="img-thumbnail"
                  />
                </div>
              )}
              
              {uploadingImage && (
                <div className="progress mt-2">
                  <div 
                    className="progress-bar" 
                    role="progressbar" 
                    style={{width: `${progress}%`}} 
                    aria-valuenow={progress} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {Math.round(progress)}%
                  </div>
                </div>
              )}
            </div>
            
            <div className="mb-3">
              <label htmlFor="content" className="form-label">Content *</label>
              <div className="editor-container border rounded">
                <EditorToolbar editor={editor} />
                <EditorContent 
                  editor={editor} 
                  className="p-3"
                  style={{minHeight: '300px'}}
                />
              </div>
            </div>
            
            <div className="d-flex gap-2 mt-4">
              <button type="submit" className="btn btn-primary" disabled={uploadingImage}>
                {formMode === 'create' ? 'Create Post' : 'Update Post'}
              </button>
              
              {formMode === 'edit' && (
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={resetForm}
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
          
          <hr className="my-5" />  
        </div>
      </section>
    </div>
  );
};

export default BlogAdmin;